import { createAppRequest } from "@/services/request";
import type { ApiRequestOptions } from "./types/common";
import type {
  FamsWorkbenchOverviewQueryParams,
  FamsWorkbenchOverviewResponse,
  FamsWorkbenchMarketDailyReport,
  FamsProductManagementYtdHeadline,
  FamsPortfolioNavShareSeries,
  FamsNavBetaComparison,
  FamsPanoramaProductHighlights,
} from "./types/workbench-overview";

const request = createAppRequest();

type ApiEnvelope<T> = {
  data?: T;
  success?: boolean;
};

const unwrapWorkbenchOverview = (
  response:
    | FamsWorkbenchOverviewResponse
    | ApiEnvelope<FamsWorkbenchOverviewResponse>
) => {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data &&
    typeof response.data === "object" &&
    "primarySnapshotDate" in response.data
  ) {
    return response.data;
  }
  return response as FamsWorkbenchOverviewResponse;
};

export async function getFamsWorkbenchOverview(
  params?: FamsWorkbenchOverviewQueryParams,
  options?: ApiRequestOptions
) {
  const response = await request<
    FamsWorkbenchOverviewResponse | ApiEnvelope<FamsWorkbenchOverviewResponse>
  >("/bff/fams/workbench-overview", {
    method: "GET",
    params,
    ...(options || {}),
  });
  return unwrapWorkbenchOverview(response);
}

export async function getFamsWorkbenchMarketDailyReport(
  params?: FamsWorkbenchOverviewQueryParams,
  options?: ApiRequestOptions
) {
  const response = await request<
    FamsWorkbenchMarketDailyReport | ApiEnvelope<FamsWorkbenchMarketDailyReport>
  >("/bff/fams/workbench-overview/market-daily-report", {
    method: "GET",
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data
  ) {
    return response.data;
  }
  return response as FamsWorkbenchMarketDailyReport;
}

export async function getFamsProductManagementYtdHeadline(
  params?: { snapshotDate?: string },
  options?: ApiRequestOptions
) {
  const response = await request<
    | FamsProductManagementYtdHeadline
    | ApiEnvelope<FamsProductManagementYtdHeadline>
  >("/bff/fams/product-management/ytd-headline", {
    method: "GET",
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data
  ) {
    return response.data;
  }
  return response as FamsProductManagementYtdHeadline;
}

export async function getFamsPortfolioNavShareSeries(
  params?: { snapshotDate?: string },
  options?: ApiRequestOptions
) {
  const response = await request<
    FamsPortfolioNavShareSeries | ApiEnvelope<FamsPortfolioNavShareSeries>
  >("/bff/fams/product-management/nav-share-series", {
    method: "GET",
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data
  ) {
    return response.data;
  }
  return response as FamsPortfolioNavShareSeries;
}

export async function getFamsPortfolioNavBetaComparison(
  params?: { snapshotDate?: string },
  options?: ApiRequestOptions
) {
  const response = await request<
    FamsNavBetaComparison | ApiEnvelope<FamsNavBetaComparison>
  >("/bff/fams/product-management/nav-beta-comparison", {
    method: "GET",
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data
  ) {
    return response.data;
  }
  return response as FamsNavBetaComparison;
}

export async function getFamsPanoramaProductHighlights(
  params?: { snapshotDate?: string },
  options?: ApiRequestOptions
) {
  const response = await request<
    FamsPanoramaProductHighlights | ApiEnvelope<FamsPanoramaProductHighlights>
  >("/bff/fams/product-management/panorama-highlights", {
    method: "GET",
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    response.data
  ) {
    return response.data;
  }
  return response as FamsPanoramaProductHighlights;
}
